// AdminWhatsappNudges — admin page that lists schools registered ≥24h
// ago with incomplete setup. Each row has a "Send WhatsApp" button that
// opens wa.me with a pre-filled Odia + English message AND marks the
// nudge as sent on the backend.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  MessageCircle, RefreshCcw, Loader2, AlertCircle, ShieldCheck, Clock, RotateCcw,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function hoursAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const hrs = Math.floor(ms / 3_600_000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function StageBadge({ stage }) {
  const cls = stage >= 3 ? "bg-amber-100 text-amber-800"
    : stage >= 1 ? "bg-rose-100 text-rose-800"
    : "bg-rose-200 text-rose-900";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${cls}`}>
      {stage}/4
    </span>
  );
}

export default function AdminWhatsappNudges() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [alreadySent, setAlreadySent] = useState([]);
  const [thresholdHours, setThresholdHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [includeSent, setIncludeSent] = useState(false);
  const [busy, setBusy] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const respList = await axios.get(`${API}/admin/whatsapp-nudges`, { params: { include_sent: includeSent } });
      setPending(respList.data?.pending || []);
      setAlreadySent(respList.data?.alreadySent || []);
      setThresholdHours(respList.data?.thresholdHours || 24);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load nudge queue");
    } finally {
      setLoading(false);
    }
  }, [includeSent]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const sendNudge = async (row) => {
    setBusy(row.id);
    // Open wa.me in a new tab first (must happen synchronously in user-gesture).
    window.open(row.whatsappLink, "_blank", "noopener,noreferrer");
    try {
      await axios.post(`${API}/admin/whatsapp-nudges/${row.id}/mark-sent`);
      toast.success(`Marked ${row.schoolName || row.udiseCode} as nudged.`);
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark as sent");
    } finally {
      setBusy(null);
    }
  };

  const resetNudge = async (row) => {
    if (!window.confirm("Re-add this school to the pending queue?")) return;
    try {
      await axios.post(`${API}/admin/whatsapp-nudges/${row.id}/reset`);
      toast.success("Reset — school will appear in the pending queue.");
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  if (user?.role !== "admin") {
    return (
      <main className="min-h-screen bg-sand-50 text-ink-900">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-600" />
          <h2 className="mt-4 text-xl font-semibold text-rose-900">Admin access required</h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">Admin · Outreach</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              WhatsApp Nudge Queue
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-700">
              Schools that registered <b>{thresholdHours}+ hours ago</b> and are still {"<"} 4/4 complete.
              Click <b>Send WhatsApp</b> to open the pre-filled Odia message on your phone — we mark it sent
              automatically once you click. Each school stays out of the queue after one nudge.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-1.5 text-xs text-ink-700">
              <input
                type="checkbox"
                checked={includeSent}
                onChange={(e) => setIncludeSent(e.target.checked)}
                data-testid="nudge-include-sent-toggle"
              />
              Show already-nudged
            </label>
            <Button onClick={fetchList} variant="outline" size="sm" disabled={loading} data-testid="nudge-refresh-btn">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4" data-testid="nudge-stat-pending">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-rose-700">Pending nudges</p>
            <p className="mt-1 font-mono text-3xl font-bold text-rose-900">{pending.length}</p>
          </div>
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4" data-testid="nudge-stat-sent">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-emerald-700">Already nudged (visible)</p>
            <p className="mt-1 font-mono text-3xl font-bold text-emerald-900">{alreadySent.length}</p>
          </div>
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-amber-700">Threshold</p>
            <p className="mt-1 font-mono text-3xl font-bold text-amber-900">{thresholdHours}h</p>
          </div>
        </div>

        {/* Pending table */}
        <section className="mb-8 overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
          <div className="border-b border-border bg-rose-50/40 px-5 py-3">
            <h2 className="text-base font-semibold text-rose-900">Pending — {pending.length}</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-clay" />
            </div>
          ) : pending.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-emerald-500" />
              <p className="mt-3 text-sm font-medium text-emerald-700">All caught up — no schools to nudge.</p>
            </div>
          ) : (
            <table className="min-w-full text-sm" data-testid="nudge-pending-table">
              <thead className="bg-sand-50 text-xs uppercase tracking-wide text-ink-700">
                <tr>
                  <th className="px-4 py-3 text-left">School</th>
                  <th className="px-4 py-3 text-left">UDISE</th>
                  <th className="px-4 py-3 text-left">Mobile</th>
                  <th className="px-4 py-3 text-left">Registered</th>
                  <th className="px-4 py-3 text-left">Stage</th>
                  <th className="px-4 py-3 text-left">Missing</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((row) => (
                  <tr key={row.id} className="border-t border-border" data-testid={`nudge-row-${row.udiseCode}`}>
                    <td className="px-4 py-3 align-top">
                      <p className="font-medium text-ink-900">{row.schoolName || "—"}</p>
                      <p className="text-xs text-ink-700">{row.schoolCategory || ""}</p>
                    </td>
                    <td className="px-4 py-3 align-top font-mono text-xs">{row.udiseCode}</td>
                    <td className="px-4 py-3 align-top font-mono text-xs">{row.mobile || "—"}</td>
                    <td className="px-4 py-3 align-top text-xs text-ink-700">
                      <Clock className="mr-1 inline h-3 w-3" />{hoursAgo(row.createdAt)}
                    </td>
                    <td className="px-4 py-3 align-top"><StageBadge stage={row.stage} /></td>
                    <td className="px-4 py-3 align-top text-xs text-ink-700">{row.missingSummary}</td>
                    <td className="px-4 py-3 text-right align-top">
                      <Button
                        onClick={() => sendNudge(row)}
                        disabled={busy === row.id || !row.mobile}
                        size="sm"
                        variant="outline"
                        className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`nudge-send-btn-${row.udiseCode}`}
                      >
                        {busy === row.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
                        Send WhatsApp
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Already-sent table */}
        {includeSent && alreadySent.length > 0 && (
          <section className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
            <div className="border-b border-border bg-emerald-50/40 px-5 py-3">
              <h2 className="text-base font-semibold text-emerald-900">Already nudged — {alreadySent.length}</h2>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-sand-50 text-xs uppercase tracking-wide text-ink-700">
                <tr>
                  <th className="px-4 py-3 text-left">School</th>
                  <th className="px-4 py-3 text-left">UDISE</th>
                  <th className="px-4 py-3 text-left">Nudged at</th>
                  <th className="px-4 py-3 text-left">Count</th>
                  <th className="px-4 py-3 text-left">Stage now</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {alreadySent.map((row) => (
                  <tr key={row.id} className="border-t border-border">
                    <td className="px-4 py-3 align-top">
                      <p className="font-medium text-ink-900">{row.schoolName || "—"}</p>
                      <p className="text-xs text-ink-700">{row.missingSummary}</p>
                    </td>
                    <td className="px-4 py-3 align-top font-mono text-xs">{row.udiseCode}</td>
                    <td className="px-4 py-3 align-top text-xs text-ink-700">{hoursAgo(row.whatsappNudgeSentAt)}</td>
                    <td className="px-4 py-3 align-top text-xs">{row.whatsappNudgeCount}×</td>
                    <td className="px-4 py-3 align-top"><StageBadge stage={row.stage} /></td>
                    <td className="px-4 py-3 text-right align-top">
                      <Button
                        onClick={() => resetNudge(row)}
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`nudge-reset-btn-${row.udiseCode}`}
                      >
                        <RotateCcw className="mr-2 h-4 w-4" /> Re-queue
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </main>
  );
}
